import React, { useEffect, useState, useRef } from 'react';
import { Shop, AppSettings, ReadingPeriod, HistoryEntry } from '../App';
import { PrinterIcon, Share2Icon, CalendarIcon } from 'lucide-react';
import { useReactToPrint } from 'react-to-print';
interface InvoiceGeneratorProps {
  shops: Shop[];
  settings: AppSettings;
  selectedPeriod: ReadingPeriod;
  history: HistoryEntry[];
}
export const InvoiceGenerator: React.FC<InvoiceGeneratorProps> = ({
  shops,
  settings,
  selectedPeriod: currentPeriod,
  history
}) => {
  // State for invoice period selection
  const [selectedPeriod, setSelectedPeriod] = useState<ReadingPeriod>(currentPeriod);
  const [showPeriodSelector, setShowPeriodSelector] = useState(false);
  const [invoiceShops, setInvoiceShops] = useState<Shop[]>(shops);
  const printRef = useRef<HTMLDivElement>(null);
  // Update invoice data when selected period changes
  useEffect(() => {
    // If we're viewing the current period, use the current shops data
    if (selectedPeriod.month === currentPeriod.month && selectedPeriod.year === currentPeriod.year) {
      setInvoiceShops(shops);
      return;
    }
    // Otherwise, try to find data in history
    const historyEntry = history.find(entry => entry.period.month === selectedPeriod.month && entry.period.year === selectedPeriod.year);
    if (historyEntry) {
      setInvoiceShops(historyEntry.shops);
    } else {
      // If no history entry found, show empty data
      setInvoiceShops(shops.map(shop => ({
        ...shop,
        currentReading: 0,
        previousReading: 0
      })));
    }
  }, [selectedPeriod, currentPeriod, shops, history]);
  const handlePrint = useReactToPrint({
    content: () => printRef.current
  });
  const calculateUsage = (shop: Shop) => {
    return Math.max(0, shop.currentReading - shop.previousReading);
  };
  const calculateBill = (shop: Shop) => {
    const usage = calculateUsage(shop);
    return usage * settings.costPerUnit + shop.additionalCharges;
  };
  // Group shops into rows of 3 for the invoice layout
  const shopRows: Shop[][] = [];
  for (let i = 0; i < invoiceShops.length; i += 3) {
    shopRows.push(invoiceShops.slice(i, i + 3));
  }
  // Format the month and year for display
  const months = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
  const billingPeriod = `${months[selectedPeriod.month]} ${selectedPeriod.year}`;
  // Generate year options (current year and 5 years before and after)
  const currentYear = new Date().getFullYear();
  const years = Array.from({
    length: 11
  }, (_, i) => currentYear - 5 + i);
  const handlePeriodChange = (newPeriod: ReadingPeriod) => {
    setSelectedPeriod(newPeriod);
    setShowPeriodSelector(false);
  };
  return <div className="space-y-4" data-id="element-57">
      <div className="flex justify-between items-center" data-id="element-58">
        <h2 className="text-xl font-semibold text-gray-800" data-id="element-59">Invoices</h2>
        <button onClick={() => setShowPeriodSelector(true)} className="flex items-center space-x-1 text-blue-600 text-sm border border-blue-300 rounded-lg px-3 py-1 hover:bg-blue-50" data-id="element-60">
          <CalendarIcon size={16} data-id="element-61" />
          <span data-id="element-62">{billingPeriod}</span>
        </button>
      </div>
      <div className="flex space-x-3 mb-4" data-id="element-63">
        <button onClick={handlePrint} className="flex-1 py-2 bg-blue-600 text-white rounded-lg flex items-center justify-center space-x-2" data-id="element-64">
          <PrinterIcon size={18} data-id="element-65" />
          <span data-id="element-66">Print Invoices</span>
        </button>
        <button className="flex-1 py-2 bg-green-600 text-white rounded-lg flex items-center justify-center space-x-2" onClick={() => {
        if (navigator.share) {
          navigator.share({
            title: 'Shop Electricity Invoices',
            text: `Generated invoices for ${billingPeriod}`
          }).catch(console.error);
        }
      }} data-id="element-67">
          <Share2Icon size={18} data-id="element-68" />
          <span data-id="element-69">Share</span>
        </button>
      </div>
      <div className="bg-white p-4 rounded-lg border border-gray-200" data-id="element-70">
        <div ref={printRef} className="print:p-0" data-id="element-71">
          <div className="text-center mb-6 print:mb-8" data-id="element-72">
            <h1 className="text-2xl font-bold" data-id="element-73">
              Shop Complex Electricity Bill
            </h1>
            <p className="text-gray-600" data-id="element-74">Billing Period: {billingPeriod}</p>
          </div>
          {shopRows.map((row, rowIndex) => <div key={rowIndex} className="flex flex-col md:flex-row gap-4 mb-8 print:flex-row print:page-break-inside-avoid" data-id="element-75">
              {row.map(shop => <div key={shop.id} className="flex-1 border border-gray-200 rounded-lg p-4 print:border-none" data-id="element-76">
                  <div className="text-center border-b pb-2 mb-3" data-id="element-77">
                    <h3 className="font-bold text-lg" data-id="element-78">{shop.name}</h3>
                    <p className="text-sm text-gray-600" data-id="element-79">
                      Invoice #{selectedPeriod.year}-{shop.id}
                    </p>
                  </div>
                  <div className="space-y-2 text-sm" data-id="element-80">
                    <div className="flex justify-between" data-id="element-81">
                      <span data-id="element-82">Previous Reading:</span>
                      <span data-id="element-83">{shop.previousReading} units</span>
                    </div>
                    <div className="flex justify-between" data-id="element-84">
                      <span data-id="element-85">Current Reading:</span>
                      <span data-id="element-86">{shop.currentReading} units</span>
                    </div>
                    <div className="flex justify-between font-medium" data-id="element-87">
                      <span data-id="element-88">Usage:</span>
                      <span data-id="element-89">{calculateUsage(shop)} units</span>
                    </div>
                    <div className="flex justify-between" data-id="element-90">
                      <span data-id="element-91">Rate per Unit:</span>
                      <span data-id="element-92">₹{settings.costPerUnit.toFixed(2)}</span>
                    </div>
                    <div className="flex justify-between" data-id="element-93">
                      <span data-id="element-94">Energy Charges:</span>
                      <span data-id="element-95">
                        ₹
                        {(calculateUsage(shop) * settings.costPerUnit).toFixed(2)}
                      </span>
                    </div>
                    <div className="flex justify-between" data-id="element-96">
                      <span data-id="element-97">Additional Charges:</span>
                      <span data-id="element-98">₹{shop.additionalCharges.toFixed(2)}</span>
                    </div>
                  </div>
                  <div className="border-t mt-3 pt-3 flex justify-between font-bold" data-id="element-99">
                    <span data-id="element-100">Total Amount:</span>
                    <span data-id="element-101">₹{calculateBill(shop).toFixed(2)}</span>
                  </div>
                </div>)}
              {/* Add empty placeholders for incomplete rows */}
              {Array.from({
            length: 3 - row.length
          }).map((_, i) => <div key={i} className="flex-1 hidden md:block print:block" data-id="element-102"></div>)}
            </div>)}
        </div>
      </div>
      {/* Period Selector Modal */}
      {showPeriodSelector && <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50" data-id="element-103">
          <div className="bg-white rounded-lg p-6 w-full max-w-sm" data-id="element-104">
            <h3 className="text-lg font-semibold mb-4" data-id="element-105">
              Select Billing Period
            </h3>
            <div className="space-y-4" data-id="element-106">
              <div data-id="element-107">
                <label className="block text-sm text-gray-600 mb-1" data-id="element-108">
                  Month
                </label>
                <select value={selectedPeriod.month} onChange={e => setSelectedPeriod({
              ...selectedPeriod,
              month: parseInt(e.target.value)
            })} className="w-full px-3 py-2 border border-gray-300 rounded-md" data-id="element-109">
                  {months.map((month, index) => <option key={month} value={index} data-id="element-110">
                      {month}
                    </option>)}
                </select>
              </div>
              <div data-id="element-111">
                <label className="block text-sm text-gray-600 mb-1" data-id="element-112">Year</label>
                <select value={selectedPeriod.year} onChange={e => setSelectedPeriod({
              ...selectedPeriod,
              year: parseInt(e.target.value)
            })} className="w-full px-3 py-2 border border-gray-300 rounded-md" data-id="element-113">
                  {years.map(year => <option key={year} value={year} data-id="element-114">
                      {year}
                    </option>)}
                </select>
              </div>
            </div>
            <div className="flex space-x-3 mt-6" data-id="element-115">
              <button onClick={() => setShowPeriodSelector(false)} className="flex-1 py-2 border border-gray-300 rounded-lg hover:bg-gray-50" data-id="element-116">
                Cancel
              </button>
              <button onClick={() => handlePeriodChange(selectedPeriod)} className="flex-1 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700" data-id="element-117">
                Apply
              </button>
            </div>
          </div>
        </div>}
    </div>;
};